<template>
	<div>
		<div
			v-if="state.error"
			class="error"
			v-html="$t(`form${capitalize(state.error)}`, { email: defaults[locale].website.email })"
		/>
		<form
			:id="name"
			ref="contactform"
			method="post"
			enctype="multipart/form-data"
			class="contact-form"
			@submit.prevent="submit()"
		>
			<div v-for="field in form" :key="field.name">
				<div v-if="field.name == 'eventDate'">
					<client-only>
						<template #fallback>
							<input
								type="text"
								:placeholder="$t(`form${capitalize(field.name)}`) + (field.required ? ' *' : '')"
							/>
						</template>
						<date-picker
							v-model:value="state.eventDate"
							value-type="format"
							format="DD-MM-YYYY"
							class=""
							:lang="datepickerLang"
							:placeholder="$t(`form${capitalize(field.name)}`) + (field.required ? ' *' : '')"
						>
							<template #icon-calendar> &nbsp; </template>
						</date-picker>
					</client-only>
				</div>
				<div v-else-if="field.edittype == 'radiogroup'" class="formgroup-container">
					<div class="formgroup-heading">
						<strong>{{ $t(`form${capitalize(field.name)}`) }}</strong>
					</div>
					<div class="radiobuttons">
						<div v-for="(value, key) in field.options" :key="key">
							<label>
								<input v-model="field.value" type="radio" :name="field.name" :value="key" />
								{{ $t(`form${capitalize(key)}`) }}
							</label>
						</div>
						<div v-if="!field.required">
							<label>
								<input type="radio" :name="field.name" value="" />{{ $t('formNoPreference') }}
							</label>
						</div>
					</div>
				</div>
				<div v-else-if="field.edittype == 'fileuploader'">
					<input :key="field.name" type="file" accept=".doc,.docx,.pdf,.odt" :name="field.name" />
				</div>
				<div v-else-if="field.edittype == 'checkbox'" class="formgroup-container">
					<div class="checkboxes">
						<label :for="field.name">
							<input
								:id="field.name"
								v-model="field.value"
								:name="field.name"
								type="checkbox"
								:checked="!!field.value"
								:placeholder="$t(`form${capitalize(field.name)}`)"
								:required="field.required"
							/>
							<span>{{ $t(`form${capitalize(field.name)}`) }}</span>
						</label>
					</div>
				</div>
				<div v-else-if="field.edittype == 'list'" class="select">
					<select :id="field.name" v-model="field.value" :required="field.required" :name="field.name">
						<option value="" selected disabled>
							{{ $t(`form${capitalize(field.name)}`) + (field.required ? ' *' : '') }}
						</option>
						<option v-for="(value, key) in field.options" :key="key" :value="value">
							{{ $t(`form${capitalize(value)}`) }}
						</option>
					</select>
				</div>
				<div v-else-if="field.edittype == 'textarea'">
					<textarea
						:key="field.name"
						v-model="field.value"
						:name="field.name"
						:placeholder="$t(`form${capitalize(field.name)}`) + (field.required ? ' *' : '')"
						:required="field.required"
					/>
				</div>
				<div v-else>
					<input
						:key="field.name"
						v-model="field.value"
						:name="field.name"
						:required="field.required"
						:placeholder="$t(`form${capitalize(field.name)}`) + (field.required ? ' *' : '')"
						:type="state.inputTypes[field.name] || 'text'"
					/>
				</div>
			</div>
			<button class="button button-cta action">{{ $t('formSubmit') }}</button>
		</form>
	</div>
</template>

<script setup>
import DatePicker from 'vue-datepicker-next';
import 'vue-datepicker-next/index.css';
import datePickerTranslations from '~/i18n/datePicker.js';

const { locale } = useI18n();
const { $installRecaptcha, $executeRecaptcha } = useNuxtApp();
const { defaults, fetchDefaults } = useDefaults();

await fetchDefaults();

const contactform = ref(null);

const props = defineProps({
	form: { type: Array, default: () => [] },
	name: { type: String, default: '' },
});

const state = useState('state', () => ({
	eventDate: '',
	inputTypes: { email: 'email', phone: 'tel' },
	error: '',
}));

const datepickerLang = computed(() => datePickerTranslations[locale.value] ?? datePickerTranslations.en);

const submit = async () => {
	const formData = new FormData();
	props.form.forEach((item) => formData.append(item.name, item.value));

	formData.append('language', locale.value);

	if (state.value.eventDate) {
		formData.append('eventDate', state.value.eventDate);
	}

	if (window.recaptcha) {
		formData.append('g-recaptcha-response', await $executeRecaptcha());
	}

	const { data: result } = await useWebsiteFetch(props.name, {
		method: 'POST',
		body: formData,
		query: { language: locale.value },
	});

	if (result.value.status === 'error') {
		state.value.error = result.value.error;
	} else if (result.value.redirect) {
		contactform.value.reset();
		navigateTo(result.value.redirect);
	}
};

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

onMounted(async () => {
	await $installRecaptcha();
});
</script>

<style lang="scss" scoped>
.mx-datepicker {
	width: 100%;
}

.grid-columns-4 form {
	grid-template-columns: 1fr 1fr 1fr;
}

.lost-found form > div:nth-last-child(4) {
	grid-column: 1 / -1;
}

form {
	margin: 0 auto;
	padding: 25px 0 0;
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 25px;

	> div:nth-last-child(2),
	> div:nth-last-child(3) {
		grid-column: 1 / -1;
	}

	.error {
		display: block;
		margin: 0 0 10px;
	}

	:deep(input:not([type='checkbox'], [type='radio'], [type='submit'])),
	select,
	textarea {
		color: var(--body-color);
		border: 1px solid #ccc;
		width: 100%;
		min-height: 55px;
		padding: 15px 50px 15px 30px;
		font-size: var(--body-font-size);
		font-family: var(--body-font-family);
		background: #fff;
		font-weight: 500;

		&::placeholder {
			color: var(--body-color);
		}
	}

	select {
		background: url('~/assets/images/icon-form-dropdown.png') no-repeat #fff;
		background-position: right 15px top 50%;
		appearance: none;
	}

	input[type='submit'] {
		width: auto;
	}

	textarea {
		min-height: 140px;
		width: 100%;
	}

	:deep(.mx-input) {
		height: auto;
		font-size: unset;
		color: unset;
		border-radius: 0;
		line-height: normal;

		&:focus {
			outline: revert;
		}
	}

	.button {
		width: auto;
		cursor: pointer;
		padding: 15px 25px;
		min-width: 190px;
		text-align: center;
	}

	.formgroup-container {
		margin: 0 0 25px;

		.formgroup-heading {
			margin-bottom: 15px;
		}

		.checkboxes {
			display: flex;
			margin: 5px 0 10px;

			&.block {
				display: block;
			}

			span {
				display: inline-block;
				width: calc(100% - 40px);
			}

			.extra-option {
				margin: 15px 0 0;
			}

			label {
				width: auto;
				padding: 0;
				font-size: 0.9em;
				margin: 0 5px 0 0;
				display: flex;
				gap: 10px;
				align-items: center;

				input {
					width: auto;
					padding: 0;
					appearance: auto;
					margin: -2px 6px 0 0;
					min-width: 20px;
				}
			}
		}

		.radiobuttons {
			display: flex;
			flex-flow: row wrap;
			margin: 5px 0 10px;

			> div {
				width: 100%;
				margin: 0 0 10px;
			}

			label,
			input {
				width: auto;
				padding: 0;
				font-size: 0.9em;
			}

			label {
				margin: 0 5px 0 0;
				display: flex;
				gap: 10px;

				input {
					width: auto;
					appearance: auto;
					margin: -2px 6px 0 0;
					padding: 0;
				}
			}
		}
	}
}

@media (max-width: 1200px) {
	form {
		gap: 15px;
	}
}

@media (max-width: 880px) {
	form {
		gap: 25px;
	}

	form > div {
		grid-column: 1 / -1;
	}
}
</style>
